import React, { useEffect, useRef, useState } from 'react';
import { format, isSameMonth, startOfMonth } from 'date-fns';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

export type MonthNavigatorVariant = 'default' | 'subtle' | 'neutral';

export interface MonthNavigatorProps {
  month: Date;
  onPreviousMonth: () => void;
  onNextMonth: () => void;
  onMonthSelect: (month: Date) => void;
  disabled?: boolean;
  className?: string;
  variant?: MonthNavigatorVariant;
}

const variantStyles: Record<
  MonthNavigatorVariant,
  {
    navBtn: string;
    pickerBtn: string;
    dateText: string;
  }
> = {
  default: {
    navBtn: 'p-2 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed',
    pickerBtn:
      'flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 text-left min-w-[220px] disabled:opacity-50 disabled:cursor-not-allowed',
    dateText: 'text-lg font-medium text-gray-900',
  },
  subtle: {
    navBtn:
      'p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
    pickerBtn:
      'flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 text-left min-w-[200px] disabled:opacity-50 disabled:cursor-not-allowed',
    dateText: 'text-sm font-semibold text-gray-900',
  },
  neutral: {
    navBtn: 'p-2 rounded-lg bg-neutral-100 hover:bg-neutral-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
    pickerBtn:
      'flex items-center gap-2 px-4 py-2 border border-neutral-300 rounded-lg bg-white hover:bg-neutral-50 text-left min-w-[220px] disabled:opacity-50 disabled:cursor-not-allowed',
    dateText: 'text-sm font-medium text-neutral-800',
  },
};

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function MonthPickerPanel({
  month,
  onSelect,
  onClose,
}: {
  month: Date;
  onSelect: (month: Date) => void;
  onClose: () => void;
}) {
  const [viewYear, setViewYear] = useState(month.getFullYear());
  const selected = startOfMonth(month);

  return (
    <div className="inline-block w-[280px] border border-neutral-200 rounded-lg p-3 bg-white">
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={() => setViewYear((y) => y - 1)}
          className="p-1 rounded hover:bg-neutral-100 text-neutral-600"
          aria-label="Previous year"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <span className="text-sm font-medium text-neutral-800">{viewYear}</span>
        <button
          type="button"
          onClick={() => setViewYear((y) => y + 1)}
          className="p-1 rounded hover:bg-neutral-100 text-neutral-600"
          aria-label="Next year"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {MONTH_LABELS.map((label, index) => {
          const candidate = startOfMonth(new Date(viewYear, index, 1));
          const isSelected = isSameMonth(candidate, selected);
          return (
            <button
              key={label}
              type="button"
              onClick={() => {
                onSelect(candidate);
                onClose();
              }}
              className={`py-2 rounded-md text-sm font-medium transition-colors ${
                isSelected
                  ? 'bg-blue-600 text-white'
                  : 'text-neutral-800 hover:bg-neutral-100'
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function MonthNavigator({
  month,
  onPreviousMonth,
  onNextMonth,
  onMonthSelect,
  disabled = false,
  className = '',
  variant = 'default',
}: MonthNavigatorProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const styles = variantStyles[variant];
  const monthLabel = format(month, 'MMMM yyyy');

  useEffect(() => {
    if (!pickerOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [pickerOpen]);

  const handlePrevious = () => {
    setPickerOpen(false);
    onPreviousMonth();
  };

  const handleNext = () => {
    setPickerOpen(false);
    onNextMonth();
  };

  return (
    <div className={`flex items-center flex-wrap gap-3 ${className}`}>
      <button
        type="button"
        onClick={handlePrevious}
        disabled={disabled}
        className={styles.navBtn}
        aria-label="Previous month"
      >
        <ChevronLeft className={variant === 'neutral' ? 'h-5 w-5 text-blue-600' : 'h-4 w-4'} />
      </button>

      <div className="relative" ref={pickerRef}>
        <button
          type="button"
          onClick={() => !disabled && setPickerOpen((open) => !open)}
          disabled={disabled}
          className={styles.pickerBtn}
          aria-label="Select month"
          aria-expanded={pickerOpen}
        >
          <Calendar className="h-5 w-5 shrink-0 text-gray-500" aria-hidden />
          <span className={styles.dateText}>{monthLabel}</span>
          <ChevronRight className="h-4 w-4 text-gray-400 ml-auto shrink-0" aria-hidden />
        </button>
        {pickerOpen && !disabled && (
          <div className="absolute left-0 top-full mt-1 z-50 shadow-lg rounded-lg">
            <MonthPickerPanel
              month={month}
              onSelect={onMonthSelect}
              onClose={() => setPickerOpen(false)}
            />
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={handleNext}
        disabled={disabled}
        className={styles.navBtn}
        aria-label="Next month"
      >
        <ChevronRight className={variant === 'neutral' ? 'h-5 w-5 text-blue-600' : 'h-4 w-4'} />
      </button>
    </div>
  );
}
